import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Heart, ImageOff, Minus, Plus } from 'lucide-react'
import { formatMoney, formatNumber } from '@najah/core'
import { Badge, Button, FullPageSpinner, cn, useToast } from '@najah/ui'
import { api, type StoreProduct } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useCart } from '../lib/cart'

export function ProductScreen({ productId, onBack }: { productId: string; onBack: () => void }) {
  const { customer } = useAuth()
  const cart = useCart()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [quantity, setQuantity] = useState(1)

  const { data: product, isPending } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api<StoreProduct>(`/products/${productId}`),
  })

  const favMutation = useMutation({
    mutationFn: () => api<{ isFavorite: boolean }>(`/favorites/${productId}`, { method: 'POST' }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['product', productId] })
      void queryClient.invalidateQueries({ queryKey: ['favorites'] })
      toast.success(res.isFavorite ? 'أُضيف للمفضّلة' : 'أُزيل من المفضّلة')
    },
    onError: () => toast.error('سجّل الدخول لاستعمال المفضّلة'),
  })

  if (isPending) return <FullPageSpinner label="جارٍ التحميل…" />
  if (!product) return null

  return (
    <div className="pb-24">
      <div className="relative aspect-square bg-bg-subtle">
        {product.images[0] ? (
          <img src={product.images[0]} alt={product.nameAr} className="size-full object-cover" />
        ) : (
          <span className="grid size-full place-items-center text-fg-subtle">
            <ImageOff className="size-12" />
          </span>
        )}

        <button
          type="button"
          onClick={onBack}
          className="absolute top-3 start-3 grid size-9 place-items-center rounded-full bg-surface/90 backdrop-blur-sm"
          aria-label="رجوع"
        >
          {/* في RTL سهم الرجوع يشير يميناً */}
          <ArrowRight className="size-5 text-fg" />
        </button>

        {customer && (
          <button
            type="button"
            onClick={() => favMutation.mutate()}
            className="absolute top-3 end-3 grid size-9 place-items-center rounded-full bg-surface/90 backdrop-blur-sm"
            aria-label={product.isFavorite ? 'إزالة من المفضّلة' : 'إضافة للمفضّلة'}
          >
            <Heart className={cn('size-5', product.isFavorite ? 'fill-danger text-danger' : 'text-fg-subtle')} />
          </button>
        )}
      </div>

      <div className="space-y-4 p-4">
        <div>
          {product.categoryName && <Badge tone="neutral">{product.categoryName}</Badge>}
          <h1 className="mt-2 font-display text-xl font-bold leading-snug text-fg">{product.nameAr}</h1>
          {product.nameFr && <p className="mt-0.5 text-sm text-fg-subtle" dir="ltr">{product.nameFr}</p>}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-primary" dir="ltr">{formatMoney(product.retailPrice)}</span>
          <span className="text-sm text-fg-subtle">/ {product.unit}</span>
          <Badge tone={product.inStock ? 'success' : 'danger'} dot className="ms-auto">
            {product.inStock ? 'متوفّر' : 'غير متوفّر'}
          </Badge>
        </div>

        {product.description && (
          <p className="text-sm leading-relaxed text-fg-muted">{product.description}</p>
        )}

        {product.book && (
          <div className="rounded-xl border border-border bg-surface p-4">
            <h2 className="mb-2 text-sm font-semibold text-fg">تفاصيل الكتاب</h2>
            <dl className="space-y-1.5 text-sm">
              {product.book.author && (
                <div className="flex justify-between">
                  <dt className="text-fg-subtle">المؤلّف</dt>
                  <dd className="font-medium text-fg">{product.book.author}</dd>
                </div>
              )}
              {product.book.publisher && (
                <div className="flex justify-between">
                  <dt className="text-fg-subtle">الناشر</dt>
                  <dd className="font-medium text-fg">{product.book.publisher}</dd>
                </div>
              )}
              {product.book.pages && (
                <div className="flex justify-between">
                  <dt className="text-fg-subtle">الصفحات</dt>
                  <dd className="font-medium text-fg" dir="ltr">{formatNumber(product.book.pages)}</dd>
                </div>
              )}
              {product.book.isbn && (
                <div className="flex justify-between">
                  <dt className="text-fg-subtle">ISBN</dt>
                  <dd className="font-mono text-xs text-fg" dir="ltr">{product.book.isbn}</dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>

      {/* شريط الإضافة مثبّت أسفل الشاشة فوق شريط التبويب */}
      <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-10 flex items-center gap-3 border-t border-border bg-surface p-3">
        <div className="flex items-center rounded-lg border border-border">
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className="px-3 py-2 text-fg-muted"
            aria-label="إنقاص"
          >
            <Minus className="size-4" />
          </button>
          <span className="w-10 text-center text-sm font-semibold tabular-nums text-fg" dir="ltr">
            {quantity}
          </span>
          <button
            type="button"
            onClick={() => setQuantity((q) => q + 1)}
            className="px-3 py-2 text-fg-muted"
            aria-label="زيادة"
          >
            <Plus className="size-4" />
          </button>
        </div>

        <Button
          size="lg"
          fullWidth
          disabled={!product.inStock}
          onClick={() => {
            cart.add(product, quantity)
            toast.success('أُضيف إلى السلة', `${product.nameAr} × ${quantity}`)
            onBack()
          }}
        >
          {product.inStock ? 'إضافة إلى السلة' : 'غير متوفّر'}
        </Button>
      </div>
    </div>
  )
}
